import React from "react";
import { ResourceEnum } from "src/constants/constants.ts";
import type { ModuleOptions } from "src/system/module.types.ts";
import type { OrderReturnRow } from "src/admin/modules/order-return/types.ts";
import type { OrderReturnCardData } from "src/admin/modules/order-return/forms/order-return/types.ts";
import { ReturnStepTitle } from "src/admin/modules/order-return/components/ReturnStepTitle.tsx";

/**
 * Возвраты. Карточка — это три шага возврата лентой: что клиент прислал, что
 * приехало, что компания решает. Лента и есть вкладки: отдельной навигации
 * нет, по шагам кликают, они меняют содержимое и адрес.
 *
 * Шаг виден всегда, даже недоступный. Исчезающий шаг ломает саму идею трёх
 * шагов: вкладка приёмки раньше пропадала у заявок «ожидается», и из
 * трёхшагового возврата оставалось два.
 *
 * Складская приёмка с телефона остаётся полноэкранным маршрутом: там сканер и
 * руки в коробке, таблицы нет.
 */
export const module: ModuleOptions<OrderReturnRow> = {
  resource: ResourceEnum.OrderReturn,
  smallIcon: <i className="icon-15-returns" />,
  bigIcon: <i className="icon-20-returns" />,
  tabs: {
    edit: {
      getTitle: (resource) => `RET-${resource.number}`,
    },
  },

  formTabs: {
    // Первый шаг — индексный маршрут карточки: прежний адрес без суффикса
    // продолжает работать, и переадресовывать неоткуда.
    erfassen: {
      title: <ReturnStepTitle step={1} />,
    },
    wareneingang: {
      title: <ReturnStepTitle step={2} />,
      // Принимать нечего, пока не отмечено, что клиент присылает. Шаг виден и
      // приглушён — доступность считает сервер, гейтом шагов.
      //
      // По `recording_done`, а не по `intake_open`: закрытый возврат гасил шаг
      // тоже, и его содержимое пропадало. Приёмка в нём была, и что приехало —
      // главный вопрос к закрытому возврату; панель для этого случая уже
      // только показывает (`isReadOnly`). Погашённая же вкладка, открытая по
      // прямой ссылке, не рисует вообще ничего: общее поведение вкладок, см.
      // `TabsFormLayout`.
      isDisabled: ({ data }) =>
        (data as OrderReturnCardData | undefined)?.steps?.recording_done ===
        false,
      disabledTooltip: ({ t }) => t("steps.2.locked"),
    },
    entscheidung: {
      title: <ReturnStepTitle step={3} />,
      // Решение принимают после приёмки: до неё оно опирается на состояние
      // товара, которого никто не видел.
      isDisabled: ({ data }) =>
        (data as OrderReturnCardData | undefined)?.steps?.decision_open ===
        false,
      disabledTooltip: ({ t }) => t("steps.3.locked"),
    },
  },
};
